import type { ZodType } from "zod";

import {
  actionReceiptSchema,
  canonicalErrorSchema,
  chatSummarySchema,
  type ActionReceipt,
  type CanonicalError,
  type ChatAction,
  type ChatSummary,
  loginResponseSchema,
  modelRegistryViewSchema,
  type ModelRegistryView,
  timelineDeltaSchema,
  timelineErrorEventSchema,
  type TimelineDelta,
  type WorkspaceView,
  workspaceViewSchema,
} from "./contracts";

const JSON_HEADERS = {
  Accept: "application/json",
  "Content-Type": "application/json",
} as const;

export class DevChatClientError extends Error {
  readonly status: number;
  readonly canonical: CanonicalError;

  constructor(status: number, canonical: CanonicalError) {
    super(canonical.message);
    this.name = "DevChatClientError";
    this.status = status;
    this.canonical = canonical;
  }
}

export class DevChatApiClient {
  #csrfToken: string | null = null;

  async login(token: string): Promise<void> {
    await this.#request("/api/v1/session", loginResponseSchema, {
      method: "POST",
      body: JSON.stringify({ token }),
      headers: JSON_HEADERS,
    });
  }

  async loadWorkspace(
    chatId?: string,
    page: WorkspacePageRequest = {},
  ): Promise<WorkspaceView> {
    const params = new URLSearchParams();
    if (chatId !== undefined) params.set("chatId", chatId);
    if (page.chatsBefore !== undefined) params.set("chatsBefore", page.chatsBefore);
    if (page.chatLimit !== undefined) params.set("chatLimit", String(page.chatLimit));
    if (page.timelineBefore !== undefined) {
      params.set("timelineBefore", page.timelineBefore);
    }
    if (page.timelineLimit !== undefined) {
      params.set("timelineLimit", String(page.timelineLimit));
    }
    const encoded = params.toString();
    const query = encoded.length === 0 ? "" : `?${encoded}`;
    const workspace = await this.#request(
      `/api/v1/workspace${query}`,
      workspaceViewSchema,
      { method: "GET", headers: { Accept: "application/json" } },
    );
    this.#csrfToken = workspace.csrfToken;
    return workspace;
  }

  async loadModelRegistry(): Promise<ModelRegistryView> {
    return this.#request(
      "/api/v1/models",
      modelRegistryViewSchema,
      { method: "GET", headers: { Accept: "application/json" } },
    );
  }

  async createChat(modelId: string, clientActionId: string): Promise<ChatSummary> {
    return this.#mutation("/api/v1/chats", chatSummarySchema, {
      modelId,
      clientActionId,
    });
  }

  async act(chatId: string, action: ChatAction): Promise<ActionReceipt> {
    return this.#mutation(
      `/api/v1/chats/${encodeURIComponent(chatId)}/actions`,
      actionReceiptSchema,
      action,
    );
  }

  watchTimeline(
    chatId: string,
    after: string,
    observer: TimelineObserver,
  ): () => void {
    const browserWindow = typeof window === "undefined" ? null : window;
    let resumeAfter = after;
    let active: ActiveTimelineSource | null = null;
    let browserOffline = browserWindow?.navigator.onLine === false;
    let disposed = false;
    let terminal = false;

    const closeActive = (): void => {
      if (active === null) return;
      const current = active;
      active = null;
      current.source.removeEventListener("timeline.v1", current.onTimeline);
      current.source.removeEventListener(
        "terminal_error.v1",
        current.onTerminalError,
      );
      current.source.onopen = null;
      current.source.onerror = null;
      current.source.close();
    };

    const failTerminal = (error: CanonicalError): void => {
      if (disposed || terminal) return;
      terminal = true;
      closeActive();
      observer.onTerminalError(error);
    };

    const connect = (): void => {
      if (disposed || terminal || browserOffline || active !== null) return;
      const url = `/api/v1/chats/${encodeURIComponent(chatId)}/events?after=${encodeURIComponent(resumeAfter)}`;
      const source = new EventSource(url, { withCredentials: true });

      const isCurrent = (): boolean =>
        !disposed && !terminal && active?.source === source;
      const onTimeline = (event: Event): void => {
        if (!isCurrent()) return;
        const message = event as MessageEvent<string>;
        let parsed: TimelineDelta;
        try {
          parsed = timelineDeltaSchema.parse(parseJsonText(message.data));
          if (!isOpaqueTimelineResumeCursor(message.lastEventId)) {
            failTerminal(localContractError("SSE_EVENT_ID_INVALID"));
            return;
          }
          if (parsed.item.chatId !== chatId) {
            failTerminal(localContractError("SSE_CHAT_SCOPE_MISMATCH"));
            return;
          }
        } catch {
          failTerminal(localContractError("INVALID_TIMELINE_EVENT"));
          return;
        }

        try {
          observer.onDelta(parsed);
          if (BigInt(parsed.cursor) > BigInt(resumeAfter)) {
            resumeAfter = parsed.cursor;
          }
        } catch {
          failTerminal(localContractError("TIMELINE_STATE_REJECTED"));
        }
      };

      const onTerminalError = (event: Event): void => {
        if (!isCurrent()) return;
        const message = event as MessageEvent<string>;
        let error: CanonicalError;
        try {
          const parsed = timelineErrorEventSchema.parse(
            parseJsonText(message.data),
          );
          error = parsed.error;
        } catch {
          error = localContractError("INVALID_TIMELINE_ERROR_EVENT");
        }
        failTerminal(error);
      };

      active = { source, onTimeline, onTerminalError };
      source.addEventListener("timeline.v1", onTimeline);
      source.addEventListener("terminal_error.v1", onTerminalError);
      source.onopen = () => {
        if (isCurrent()) observer.onOpen();
      };
      source.onerror = () => {
        if (isCurrent()) observer.onTransportError();
      };
    };

    const onBrowserOffline = (): void => {
      if (disposed || terminal || browserOffline) return;
      browserOffline = true;
      closeActive();
      observer.onTransportError();
    };
    const onBrowserOnline = (): void => {
      if (disposed || terminal || !browserOffline) return;
      browserOffline = false;
      connect();
    };
    browserWindow?.addEventListener("offline", onBrowserOffline);
    browserWindow?.addEventListener("online", onBrowserOnline);
    if (browserOffline) observer.onTransportError();
    else connect();

    return () => {
      disposed = true;
      browserWindow?.removeEventListener("offline", onBrowserOffline);
      browserWindow?.removeEventListener("online", onBrowserOnline);
      closeActive();
    };
  }

  async #mutation<T>(
    path: string,
    schema: ZodType<T>,
    body: object,
  ): Promise<T> {
    if (this.#csrfToken === null) {
      throw new DevChatClientError(0, localContractError("CSRF_TOKEN_NOT_READY"));
    }
    return this.#request(path, schema, {
      method: "POST",
      body: JSON.stringify(body),
      headers: { ...JSON_HEADERS, "X-CSRF-Token": this.#csrfToken },
    });
  }

  async #request<T>(
    path: string,
    schema: ZodType<T>,
    init: RequestInit,
  ): Promise<T> {
    let response: Response;
    try {
      response = await fetch(path, {
        ...init,
        credentials: "same-origin",
        cache: "no-store",
      });
    } catch {
      throw new DevChatClientError(0, localTransportError());
    }

    const raw: unknown = await readJson(response);
    if (!response.ok) {
      const parsed = canonicalErrorFromUnknown(raw);
      throw new DevChatClientError(response.status, parsed);
    }

    const parsed = schema.safeParse(raw);
    if (!parsed.success) {
      throw new DevChatClientError(
        response.status,
        localContractError("INVALID_SERVER_RESPONSE"),
      );
    }
    return parsed.data;
  }
}

function isOpaqueTimelineResumeCursor(value: string): boolean {
  return (
    value.length > 0 &&
    value.length <= 1_024 &&
    /^[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+$/.test(value)
  );
}

export interface WorkspacePageRequest {
  chatsBefore?: string;
  chatLimit?: number;
  timelineBefore?: string;
  timelineLimit?: number;
}

export interface TimelineObserver {
  onOpen: () => void;
  onDelta: (delta: TimelineDelta) => void;
  onTransportError: () => void;
  onTerminalError: (error: CanonicalError) => void;
}

interface ActiveTimelineSource {
  source: EventSource;
  onTimeline: EventListener;
  onTerminalError: EventListener;
}

async function readJson(response: Response): Promise<unknown> {
  try {
    const raw: unknown = await response.json();
    return raw;
  } catch {
    if (response.ok) {
      return null;
    }
    return {
      error: localContractError("INVALID_ERROR_RESPONSE"),
    };
  }
}

function parseJsonText(value: string): unknown {
  const raw: unknown = JSON.parse(value);
  return raw;
}

function canonicalErrorFromUnknown(raw: unknown): CanonicalError {
  const envelope = zodEnvelope(raw);
  return envelope ?? localContractError("INVALID_ERROR_RESPONSE");
}

function zodEnvelope(raw: unknown): CanonicalError | null {
  if (typeof raw !== "object" || raw === null || !("error" in raw)) {
    return null;
  }
  const parsed = canonicalErrorSchema.safeParse((raw as { error: unknown }).error);
  return parsed.success ? parsed.data : null;
}

function localTransportError(): CanonicalError {
  return {
    code: "NETWORK_UNAVAILABLE",
    category: "transport",
    retryable: true,
    safeToRetryWithSameIdempotencyKey: true,
    message: "The Dev Chat backend is unreachable.",
    details: {},
  };
}

function localContractError(code: string): CanonicalError {
  return {
    code,
    category: "contract",
    retryable: false,
    safeToRetryWithSameIdempotencyKey: false,
    message: "Dev Chat received an incompatible response.",
    details: {},
  };
}

export function toCanonicalError(error: unknown): CanonicalError {
  if (error instanceof DevChatClientError) {
    return error.canonical;
  }
  return localContractError("UNEXPECTED_CLIENT_ERROR");
}

export function isUnauthorized(error: unknown): boolean {
  return error instanceof DevChatClientError && error.status === 401;
}
